/**
 * MonthCalendar — Block
 *
 * 6-row × 7-column month grid. Each day cell shows the day's appointment
 * EVENT CARDS (not just a count): a left profile-color bar + patient name +
 * time, or a striped grey block card with its reason — capped with a
 * "+N more" affordance. Clicking an event opens that appointment; clicking
 * "+N more", the date, or empty space drops to Day view on that date.
 *
 * Flutter parity: `calendar_by_month_widget.dart` — the month overview
 * renders each day's `appointmentList` as `CalendarCardWidget`s + a
 * "+N more" card (`onMoreClick`). Earlier React builds showed only a count +
 * status dots; this matches the Flutter event-card layout.
 *
 * Grid behavior:
 *   - Always renders 6 rows (42 cells) so the layout doesn't reflow.
 *   - Weeks start on MONDAY — Flutter PM month range is
 *     `DateTime(y,m,1).subtract(weekday-1)` → the Monday of the week
 *     containing the 1st (`calendar_tab.dart:718-725`).
 *   - Prev/next-month cells render dimmed.
 *   - Today gets a filled brand-primary disc behind the day number.
 *   - Selected day gets a brand-depth ring.
 */

import { Ban, Repeat } from 'lucide-react';
import { categoryColor } from '@/shared/lib/category-color';
import { cn } from '@/lib/utils';
import { BLOCK_STRIPES } from '@/components/cards/appointment-tile';

/**
 * A single appointment shown inside a month-grid day cell. Slim projection of
 * `DayListRow` — just what the chip renders + the ids needed to open the
 * appointment.
 */
export interface MonthEvent {
  id: string;
  /** Patient name, or block reason for a block entry. */
  label: string;
  /** "9:00a - 9:30a". */
  timeRange: string;
  /** appointmentProfile.color → the left bar. Falls back to brand primary. */
  profileColor?: string;
  isBlock?: boolean;
  isRecurring?: boolean;
  // Ids for opening the appointment view (same route as the calendar click).
  patientId?: string;
  providerId?: string;
  appointmentProfileId?: string;
}

export interface MonthCalendarDay {
  /** Local-time YYYY-MM-DD — drives the key + click payload. */
  iso: string;
  /** 1–31. */
  date: number;
  /** True when this cell falls inside the focused month. */
  inMonth: boolean;
  /** True when this cell is today (local time). */
  isToday: boolean;
  /** True when this cell matches the parent's selected date. */
  isSelected: boolean;
  /** Appointments + blocks to render on this day, sorted by start. */
  events: MonthEvent[];
  /**
   * Backend-provided appointment overflow ("+N more") — the number of
   * appointments NOT included in `events` (the API caps each day at 3).
   * NOT computed from `events.length`. Blocks are always shown in full and
   * don't count toward this.
   */
  moreCount: number;
}

interface MonthCalendarProps {
  /** 42 entries (6×7). Generate via {@link buildMonthGrid}. */
  days: MonthCalendarDay[];
  /** Month label rendered above the grid — e.g. "MAY 2026". */
  monthLabel: string;
  isLoading?: boolean;
  isError?: boolean;
  /** Drop to Day view on a date (date header / "+N more" / empty space). */
  onDayClick?: (iso: string) => void;
  /** Open a specific appointment from its event card. */
  onEventClick?: (event: MonthEvent) => void;
}

const WEEKDAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

export function MonthCalendar({
  days,
  monthLabel,
  isLoading = false,
  isError = false,
  onDayClick,
  onEventClick,
}: MonthCalendarProps) {
  // Read at render, not at module load: a tab left open past midnight still greys the right days.
  // An event on an earlier day is past and drawn muted (practitioner calendar).
  const today = toLocalISO(new Date());
  return (
    <div aria-label={monthLabel} className="flex flex-col flex-1 min-h-0 bg-surface-card">
      {/* The period is named by the toolbar ("October 2026", practitioner calendar), so no uppercase eyebrow here; only a
          quiet line while loading or on error. */}
      {isLoading || isError ? (
        <div className="px-4 py-2 border-b border-border-default">
          {isLoading ? <span className="text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-text-muted">Loading…</span> : null}
          {isError ? (
            <span className="text-[length:var(--type-meta-size)] leading-[var(--type-meta-line)] text-status-error" role="alert">
              Couldn't load — try toggling the date.
            </span>
          ) : null}
        </div>
      ) : null}

      {/* Weekday header row */}
      <div className="sticky top-0 z-10 grid grid-cols-7 border-b border-border-default bg-surface-card">
        {WEEKDAY_LABELS.map((label) => (
          <div
            key={label}
            className={cn('flex h-10 items-center px-3 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] text-text-secondary', label !== 'Mon' && 'border-l border-border-subtle')}
          >
            {label}
          </div>
        ))}
      </div>

      {/* 6-row cell grid — scrolls vertically if a busy month overflows. */}
      <div className="grid grid-cols-7 grid-rows-6 flex-1 min-h-0 overflow-y-auto scrollbar-hide">
        {days.map((day, i) => (
          <MonthCell
            key={day.iso}
            day={day}
            weekend={i % 7 > 4}
            past={day.iso < today}
            isToday={day.iso === today}
            onDayClick={onDayClick}
            onEventClick={onEventClick}
            isLoading={isLoading}
          />
        ))}
      </div>
    </div>
  );
}

interface MonthCellProps {
  day: MonthCalendarDay;
  /** Saturday or Sunday: the panel grey, like days outside the month (practitioner calendar). */
  weekend?: boolean;
  /** Before today: events lose their profile colour. */
  past?: boolean;
  /** The cell is today, judged at render (the grid's own `isToday` is as old as the grid's memo). */
  isToday?: boolean;
  onDayClick?: (iso: string) => void;
  onEventClick?: (event: MonthEvent) => void;
  isLoading?: boolean;
}

function MonthCell({ day, weekend, past, isToday, onDayClick, onEventClick, isLoading }: MonthCellProps) {
  // Render every event we hold (the backend already capped appointments at 3
  // per day; blocks are always shown). "+N more" comes from the backend
  // overflow `day.moreCount`, NOT from slicing here.
  const goToDay = () => {
    if (!isLoading) onDayClick?.(day.iso);
  };

  return (
    // Cell is a div (not a button) so the event chips can be real buttons —
    // nested <button>s are invalid HTML. Empty-space click drops to Day view.
    <div
      onClick={goToDay}
      className={cn(
        'flex flex-col items-stretch text-left p-2 gap-1 border-r border-b border-border-subtle last:border-r-0 min-h-[112px]',
        // Outside the month and weekends: the panel grey, by colour not opacity (practitioner calendar, Antonin 13 Oct).
        // No selected-day ring: the Month view's period is the month; today keeps its blue disc.
        day.inMonth && !weekend ? 'bg-surface-card' : 'bg-grey-100',
        !isLoading && 'cursor-pointer hover:bg-blue-100/30',
        isLoading && 'animate-pulse',
      )}
    >
      {/* Day number — today gets a filled disc */}
      <div className="flex items-center justify-between gap-1">
        <span
          className={cn(
            'inline-flex h-6 min-w-6 items-center justify-center rounded-full px-1 text-[length:var(--type-label-size)] font-medium leading-[var(--type-label-line)] tabular-nums',
            isToday
              ? 'bg-brand-primary text-text-inverse'
              : day.inMonth
                ? 'text-text-primary'
                : 'text-text-hint',
          )}
        >
          {day.date}
        </span>
      </div>

      {/* Event cards */}
      <div className="flex flex-col gap-1 min-w-0">
        {day.events.map((event) => (
          <MonthEventChip key={event.id} event={event} past={past} onClick={onEventClick} />
        ))}
        {day.moreCount > 0 ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              goToDay();
            }}
            className="rounded-sm px-1 py-0.5 text-left text-xs font-semibold text-brand-primary hover:bg-blue-100/50"
          >
            +{day.moreCount} more
          </button>
        ) : null}
      </div>
    </div>
  );
}

function MonthEventChip({
  event,
  past,
  onClick,
}: {
  event: MonthEvent;
  past?: boolean;
  onClick?: (event: MonthEvent) => void;
}) {
  const handle = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClick?.(event);
  };

  if (event.isBlock) {
    const label = event.label?.trim() || 'Blocked';
    return (
      <button
        type="button"
        onClick={handle}
        // PROD-794: block "reason" is staff-typed free text that can carry PHI
        // (e.g. a patient name). Keep it out of the native `title` (tooltip /
        // a11y tree) — the visible chip body below still shows it.
        title={`Blocked · ${event.timeRange}`}
        className={cn(
          'flex w-full items-center gap-1 overflow-hidden rounded-sm px-1 py-0.5 text-left hover:shadow-sm',
          BLOCK_STRIPES,
        )}
      >
        {event.isRecurring ? (
          <Repeat className="h-2 w-2 shrink-0 text-text-muted" aria-label="Recurring block" />
        ) : (
          <Ban className="h-2 w-2 shrink-0 text-text-muted" />
        )}
        <span className="flex-1 truncate text-xs font-medium text-text-secondary">
          {label}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={handle}
      // PHI-FREE — event.label is the patient name for non-block entries;
      // title attrs are browser-tooltip/a11y-tree accessible (PROD-794 pattern).
      // The name stays in the visible chip body where the staff-only
      // viewport permission already gates exposure.
      title={event.timeRange}
      className="flex w-full items-center gap-1 overflow-hidden rounded-sm border border-border-subtle bg-surface-card px-1 py-0.5 text-left hover:bg-grey-100"
    >
      <span
        className="h-3 w-0.5 shrink-0 rounded-full"
        // Past: no saturation; colour is for the work ahead (practitioner calendar).
        style={{ backgroundColor: past ? 'var(--color-text-hint)' : categoryColor(event.profileColor, 500, 'var(--cat-blue-500)') }}
        aria-hidden
      />
      <span className={cn('flex-1 truncate text-xs font-medium', past ? 'text-text-secondary' : 'text-text-primary')}>
        {event.label || 'Unknown'}
      </span>
      <span className="shrink-0 text-xs text-text-muted">{event.timeRange}</span>
    </button>
  );
}

// ─── grid builder ──────────────────────────────────────────────────────

interface BuildMonthGridOptions {
  /** A date inside the month to render. Local time. */
  monthAnchor: Date;
  /** Selected date for the ring marker. */
  selectedISO?: string;
  /**
   * `iso -> { events, moreCount }`. `events` are the rendered chips
   * (appointments capped at 3 by the backend + all blocks, pre-sorted);
   * `moreCount` is the backend appointment overflow for "+N more".
   */
  dayData: Map<string, { events: MonthEvent[]; moreCount: number }>;
  /** Today's local-ISO — pre-computed so the function stays pure. */
  todayISO: string;
}

/**
 * Generates the 42-entry grid for a given month. Padding cells before the 1st
 * come from the previous month; trailing cells from the next. Weeks start on
 * MONDAY (Flutter PM convention — `calendar_tab.dart:718-725`).
 */
export function buildMonthGrid({
  monthAnchor,
  selectedISO,
  dayData,
  todayISO,
}: BuildMonthGridOptions): MonthCalendarDay[] {
  const focusYear = monthAnchor.getFullYear();
  const focusMonth = monthAnchor.getMonth(); // 0-11

  // First cell = Monday on or before the 1st. JS getDay(): Sun=0…Sat=6, so
  // days-since-Monday = (getDay() + 6) % 7 (Mon→0, Sun→6).
  const firstOfMonth = new Date(focusYear, focusMonth, 1);
  const firstCell = new Date(firstOfMonth);
  const mondayOffset = (firstOfMonth.getDay() + 6) % 7;
  firstCell.setDate(1 - mondayOffset);

  const out: MonthCalendarDay[] = [];
  const cursor = new Date(firstCell);
  for (let i = 0; i < 42; i++) {
    const iso = toLocalISO(cursor);
    const data = dayData.get(iso);
    out.push({
      iso,
      date: cursor.getDate(),
      inMonth: cursor.getMonth() === focusMonth,
      isToday: iso === todayISO,
      isSelected: iso === selectedISO,
      events: data?.events ?? [],
      moreCount: data?.moreCount ?? 0,
    });
    cursor.setDate(cursor.getDate() + 1);
  }
  return out;
}

export function toLocalISO(d: Date): string {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}
